import posthog from 'posthog-js';
import { AnalyticsEvent, EventProperties, Surface, events, safeRoute, replayEligible, sanitizeRecording } from './contract';
export type { AnalyticsEvent, EventProperties } from './contract';
export const CONSENT_EVENT = 'verfi:analytics-consent';
const COOKIE = 'verfi_analytics_v1';
const productionHosts = ['verfi.io', 'www.verfi.io', 'app.verfi.io', 'proof.verfi.io'];
export type ConsentChoice = { accepted: boolean; device: string; version: number };
function cookieName(): string {
  return productionHosts.includes(location.hostname) ? COOKIE : `${COOKIE}_nonprod`;
}
function cookieValues(): string[] {
  return document.cookie.split(';').map(c => c.trim())
    .filter(c => c.startsWith(`${cookieName()}=`)).map(c => c.slice(cookieName().length + 1));
}
function readChoiceValue(): string | undefined {
  const values = cookieValues();
  // Cookie headers expose neither domain nor path. Never trust their order.
  // Conflicting legacy values cannot grant consent; withdrawal wins.
  return values.find(v => v.split('.')[0] === 'declined')
    || (values.length && values.every(v => v === values[0]) ? values[0] : undefined);
}
let config: { token: string; environment: string; surface: Surface; release?: string } | undefined;
let initialized = false;
let identityPending = false;
let optedIn = false;
let identity: { user: string; tenant: string } | undefined;
let lastPage = '';
let recording = false;
// Auth is an App-only exclusion; Website and Proof retain their existing policy.
function authRouteBlocked(): boolean {
  return config?.surface === 'app' && /^\/(?:app\/)?(?:sign-in|sign-up)(?:\/|$)/.test(location.pathname);
}
function stopCapture(): void {
  if (initialized && optedIn) { posthog.stopSessionRecording(); posthog.opt_out_capturing(); posthog.reset(true); }
  optedIn = false; recording = false; lastPage = '';
}
const acquisitionSources = ['direct', 'google', 'bing', 'linkedin', 'facebook', 'x', 'email', 'other'];
function acquisitionSource(): string {
  const campaign = new URLSearchParams(location.search).get('utm_source')?.toLowerCase();
  if (campaign) return acquisitionSources.includes(campaign) ? campaign : 'other';
  if (!document.referrer) return 'direct';
  try {
    const host = new URL(document.referrer).hostname;
    for (const [domain, source] of [['google.com', 'google'], ['bing.com', 'bing'], ['linkedin.com', 'linkedin'], ['facebook.com', 'facebook'], ['t.co', 'x']]) {
      if (host === domain || host.endsWith(`.${domain}`)) return source;
    }
  } catch { /* Do not send malformed referrers. */ }
  return 'other';
}

export function consent(): 'accepted' | 'declined' | null {
  if (typeof document === 'undefined') return null;
  const value = readChoiceValue()?.split('.')[0];
  return value === 'accepted' || value === 'declined' ? value : null;
}
/** Per-browser ordering: no wall-clock comparison across devices. Shared first-party cookie. */
export function consentChoice(): ConsentChoice | null {
  const value = consent();
  if (!value) return null;
  const raw = readChoiceValue();
  const [, device, sequence] = (raw || '').split('.');
  const version = Number(sequence);
  if (/^[a-f0-9-]{36}$/.test(device || '') && Number.isSafeInteger(version) && version >= 1) {
    return { accepted: value === 'accepted', device, version };
  }
  // Migrate a legacy explicit choice, never invent a choice for a new browser.
  const choice = { accepted: value === 'accepted', device: crypto.randomUUID(), version: 1 };
  writeChoice(choice);
  return choice;
}
function writeChoice(choice: ConsentChoice): void {
  const domain = productionHosts.includes(location.hostname) ? '; Domain=verfi.io' : '';
  document.cookie = `${cookieName()}=${choice.accepted ? 'accepted' : 'declined'}.${choice.device}.${choice.version}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${location.protocol === 'https:' ? '; Secure' : ''}`;
}
export function setConsent(value: 'accepted' | 'declined'): void {
  const previous = consentChoice();
  // Advance beyond every visible version for this device, including shadowed legacy values.
  const versions = cookieValues().map(raw => raw.split('.'))
    .filter(([, device, version]) => device === previous?.device && Number.isSafeInteger(Number(version)))
    .map(([, , version]) => Number(version));
  const choice = { accepted: value === 'accepted', device: previous?.device || crypto.randomUUID(), version: Math.max(previous?.version || 0, ...versions) + 1 };
  // Remove only this production host's old root cookie; never touch production from staging.
  if (productionHosts.includes(location.hostname)) {
    document.cookie = `${COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
  }
  writeChoice(choice);
  syncConsent();
  // Persist withdrawal even if the App is closed. This is a first-party preference
  // update, not telemetry; the endpoint authenticates the current App session.
  if (['verfi.io', 'www.verfi.io', 'proof.verfi.io'].includes(location.hostname)) {
    // Next uses trailingSlash; a redirect on a cross-origin preflight is blocked.
    void fetch('https://app.verfi.io/api/analytics/context/', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(choice),
    }).catch(() => { /* Offline preferences synchronize on the next App visit. */ });
  }
  window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_EVENT, { detail: choice }));
}
export function setupAnalytics(options: NonNullable<typeof config>): void {
  if (config) { syncConsent(); return; }
  // A production token must never be enabled in a preview, staging or local build.
  if (!options.token || !/^phc_[A-Za-z0-9]+$/.test(options.token) || options.environment !== 'production' || !productionHosts.includes(location.hostname)) return;
  config = options;
  window.addEventListener('focus', syncConsent);
  document.addEventListener('visibilitychange', syncConsent);
  // Cookies do not emit storage events between subdomains. Stop capture promptly after withdrawal elsewhere.
  let observed = consent();
  window.setInterval(() => {
    const current = consent();
    syncConsent();
    if (current !== observed) { observed = current; window.dispatchEvent(new Event(CONSENT_EVENT)); }
  }, 1000);
  syncConsent();
}
function syncConsent(): void {
  try { syncConsentUnsafe(); } catch { /* Analytics never blocks product or consent UI. */ }
}
function syncConsentUnsafe(): void {
  if (!config) return;
  // Stop even while account context is pending or consent was changed in another tab.
  if (authRouteBlocked() || consent() !== 'accepted') { stopCapture(); return; }
  if (identityPending) return;
  if (!initialized) {
    const options = config;
    posthog.init(options.token, {
      api_host: 'https://us.i.posthog.com', ui_host: 'https://us.posthog.com',
      autocapture: false, capture_pageview: false, capture_pageleave: false,
      advanced_disable_feature_flags: true, advanced_disable_feature_flags_on_first_load: true, save_campaign_params: false, save_referrer: false, ip: false,
      capture_dead_clicks: false, capture_performance: false, capture_exceptions: false,
      disable_surveys: true, enable_recording_console_log: false, disable_session_recording: true,
      person_profiles: 'identified_only', cross_subdomain_cookie: true, persistence: 'localStorage+cookie', cookie_persisted_properties: ['acquisition_source'],
      opt_out_capturing_by_default: true, disable_external_dependency_loading: false,
      session_recording: {
        maskAllInputs: true, maskTextSelector: '*', blockClass: 'ph-no-capture',
        blockSelector: '[data-private], iframe, canvas, video, audio, img, svg, pre, code, input, textarea, select, [contenteditable], script',
        recordCrossOriginIframes: false, recordHeaders: false, recordBody: false,
        captureCanvas: { recordCanvas: false },
        // rrweb also calls this hook with {name} for the page's Meta event.
        // Keep safe page metadata (required for playback); discard network entries.
        maskCapturedNetworkRequestFn: request => Object.keys(request).length === 1 && 'name' in request
          ? { ...request, name: `${location.origin}${safeRoute(location.pathname, options.surface)}` } : null,
        compress_events: false, inlineStylesheet: false, slimDOMOptions: 'all',
      },
      before_send: event => {
        if (!event || authRouteBlocked() || identityPending || consent() !== 'accepted') return null;
        const name = event.event;
        delete event.$set;
        delete event.$set_once;
        if (name === '$snapshot') {
          if (!replayEligible(location.pathname, options.surface)) return null;
          event.properties = {
            token: options.token,
            distinct_id: event.properties.distinct_id,
            $session_id: event.properties.$session_id, $window_id: event.properties.$window_id,
            $snapshot_data: sanitizeRecording(event.properties.$snapshot_data),
            $snapshot_bytes: event.properties.$snapshot_bytes,
          };
          return event;
        }
        if (!['$pageview', '$identify', '$set'].includes(name) && !(events as readonly string[]).includes(name)) return null;
        const source = event.properties;
        // Rebuild rather than blacklist: SDK defaults include full URL, referrer and campaign strings.
        event.properties = {
            token: options.token,
          distinct_id: source.distinct_id, $device_id: source.$device_id,
          $session_id: source.$session_id, $window_id: source.$window_id,
          $anon_distinct_id: source.$anon_distinct_id, $insert_id: source.$insert_id,
          acquisition_source: acquisitionSources.includes(source.acquisition_source) ? source.acquisition_source : 'other',
          $process_person_profile: !!identity, surface: options.surface, environment: options.environment,
          release: options.release?.replace(/[^a-f0-9]/gi, '').slice(0, 40),
          route: safeRoute(location.pathname, options.surface),
          $current_url: `${location.origin}${safeRoute(location.pathname, options.surface)}`,
          ...(identity ? { tenant_id: identity.tenant, customer_type: 'authenticated' } : { customer_type: 'anonymous' }),
          ...(['load', 'playback', 'unlock', 'checkout'].includes(source.operation) ? { operation: source.operation } : {}),
          ...(['success', 'failure'].includes(source.outcome) ? { outcome: source.outcome } : {}),
        };
        return event;
      },
    });
    initialized = true;
    if (options.surface === 'app' && posthog.get_property('$user_id') && posthog.get_distinct_id() !== identity?.user) posthog.reset(true);
  }
  if (!optedIn) {
    posthog.opt_in_capturing({ captureEventName: false });
    optedIn = true;
    posthog.register_once({ acquisition_source: acquisitionSource() });
    if (identity) posthog.identify(identity.user);
  }
  pageView();
}
/** Suspend during Clerk account transitions so async context resolution cannot attribute to the old user. */
export function suspendIdentity(): void {
  identityPending = true;
  try { if (initialized) posthog.stopSessionRecording(); } catch { /* Optional telemetry. */ }
  recording = false;
}
export function setIdentity(user?: string, tenant?: string): void {
  identityPending = false;
  if (identity?.user === user && identity?.tenant === tenant && !(initialized && posthog.get_property('$user_id') && posthog.get_distinct_id() !== user)) return;
  if (initialized && (identity || posthog.get_property('$user_id'))) { posthog.stopSessionRecording(); posthog.reset(true); optedIn = false; recording = false; lastPage = ''; }
  identity = user && tenant ? { user, tenant } : undefined;
  if (authRouteBlocked()) { syncConsent(); return; }
  if (initialized && consent() === 'accepted') {
    if (!optedIn) syncConsent();
    else if (identity) posthog.identify(identity.user);
  }
}
export function pageView(): void {
  try { pageViewUnsafe(); } catch { /* Optional analytics must not affect navigation. */ }
}
function pageViewUnsafe(): void {
  if (authRouteBlocked()) { stopCapture(); return; }
  if (!optedIn) { syncConsent(); return; }
  if (!initialized || !config || identityPending || consent() !== 'accepted') return;
  const route = safeRoute(location.pathname, config.surface);
  // Never record auth, embedded playgrounds or evidence detail pages, even with masking.
  const eligible = replayEligible(location.pathname, config.surface);
  if (eligible && !recording) { posthog.startSessionRecording(); recording = true; }
  if (!eligible && recording) { posthog.stopSessionRecording(); recording = false; }
  const page = location.pathname; // only held locally; route templates are sent
  if (page === lastPage) return;
  lastPage = page;
  posthog.capture('$pageview');
  if (config.surface === 'website' && route === '/pricing') track('pricing_viewed');
  if (config.surface === 'app' && route === '/settings/api-keys') track('api_keys_viewed');
}
export function track(event: AnalyticsEvent, properties: EventProperties = {}): void {
  if (!initialized || authRouteBlocked() || identityPending || consent() !== 'accepted') return;
  try { posthog.capture(event, properties); } catch { /* Analytics must not affect the product. */ }
}
